import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { invalidateQueryKeys } from './invalidateQueryKeys'

interface RedrawWinnerData {
  sweepstakeDrawingId: number
  redrawReason: string
  sweepstakeWinnerId?: number
}

interface UseRedrawWinnerProps {
  onSuccess?: (data: undefined, variables: RedrawWinnerData) => void
  onError?: (error: Error, variables: RedrawWinnerData) => void
  invalidateQueryKeys?: readonly QueryKey[]
}

export const useRedrawWinner = ({
  onSuccess,
  onError,
  invalidateQueryKeys: queryKeysToInvalidate,
}: UseRedrawWinnerProps): UseMutationResult<
  undefined,
  Error,
  RedrawWinnerData
> => {
  const redrawWinner = useTypedApiClient({
    path: '/admin/sweepstake-winners/nextWinner/{sweepstakeDrawingId}',
    method: 'put',
  })

  const queryClient = useQueryClient()

  return useMutation<undefined, Error, RedrawWinnerData>({
    mutationFn: async ({
      sweepstakeDrawingId,
      redrawReason,
      sweepstakeWinnerId,
    }: RedrawWinnerData) => {
      await redrawWinner({
        redrawReason,
        sweepstakeDrawingId,
        sweepstakeWinnerId,
      })
    },
    onSuccess: async (data, variables) => {
      if (
        queryKeysToInvalidate !== undefined &&
        queryKeysToInvalidate.length > 0
      ) {
        await invalidateQueryKeys(queryClient, queryKeysToInvalidate)
      } else {
        await queryClient.invalidateQueries({
          queryKey: ['/sweepstakes'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['/sweepstakes/drawing-info/multi-draw'],
        })
      }

      onSuccess?.(data, variables)
    },
    onError,
  })
}
